import { z } from 'zod';

import { defineCommand } from '@nesso/common-contracts/define';
import { practiceIdSchema, practiceMemberIdSchema } from '@nesso/common-contracts/ids';
import { metadata } from '@nesso/common-contracts/schema-metadata';

import { sessionIdSchema, sessionNoteIdSchema } from './ids';
import { sessionNoteOutputSchema } from './session.models';
import {
    formatSettingsSchema,
    sessionNoteContentSchema,
    sessionNoteStatusSchema,
    sessionNoteTypeSchema,
} from './session-note.models';

export const SESSION_NOTE_GENERATE_TYPE = 'session-note/generate';
export const SESSION_NOTE_UPDATE_TYPE = 'session-note/update';
export const SESSION_NOTE_WRITE_TYPE = 'session-note/write';
export const SESSION_NOTE_SET_FEEDBACK_TYPE = 'session-note/set-feedback';
export const SESSION_NOTE_SCRIBE_CONTENT_TYPE = 'session-note/scribe-content';

export const sessionNoteGenerate = defineCommand(
    SESSION_NOTE_GENERATE_TYPE,
    z.object({
        sessionId: sessionIdSchema.meta(metadata.nonSensitive),
        practiceId: practiceIdSchema.meta(metadata.nonSensitive),
        practiceMemberId: practiceMemberIdSchema.meta(metadata.nonSensitive),
        settings: formatSettingsSchema.optional().meta(metadata.nonSensitive),
        type: sessionNoteTypeSchema.default('SOAP').meta(metadata.nonSensitive),
    }),
    z.object({ id: z.uuid() }),
);

export const sessionNoteUpdate = defineCommand(
    SESSION_NOTE_UPDATE_TYPE,
    z.object({
        id: sessionNoteIdSchema,
        practiceMemberId: practiceMemberIdSchema.meta(metadata.nonSensitive),
        status: sessionNoteStatusSchema.meta(metadata.nonSensitive),
    }),
    z.object({ id: z.uuid(), updatedAt: z.date() }),
);

export const sessionNoteWriteBaseSchema = z.object({
    id: z.uuid().optional(),
    sessionId: z.uuid().meta(metadata.nonSensitive),
    practiceMemberId: z.uuid().meta(metadata.nonSensitive),
});

export const sessionNoteWrite = defineCommand(
    SESSION_NOTE_WRITE_TYPE,
    sessionNoteWriteBaseSchema.and(sessionNoteContentSchema),
    sessionNoteOutputSchema,
);

// Published for the client to enforce, deliberately not enforced here: rejecting an over-long
// comment would throw away the rating with it. The application layer trims to it instead.
export const NOTE_FEEDBACK_COMMENT_MAX_LENGTH = 5000;

// Transport-size guards (DynamoDB 400 KB, SNS 256 KB), not a vocabulary limit — deliberately
// far above it so an unknown reason id still survives intact.
export const NOTE_FEEDBACK_REASONS_MAX_COUNT = 20;
export const NOTE_FEEDBACK_REASON_MAX_LENGTH = 200;

export const sessionNoteSetFeedback = defineCommand(
    SESSION_NOTE_SET_FEEDBACK_TYPE,
    z.object({
        id: sessionNoteIdSchema,
        practiceMemberId: practiceMemberIdSchema.meta(metadata.nonSensitive),
        feedback: z.boolean().nullable(),
        reasons: z.array(z.string()).optional(),
        comment: z.string().optional(),
    }),
);

export const sessionNoteScribeContent = defineCommand(
    SESSION_NOTE_SCRIBE_CONTENT_TYPE,
    z.object({
        sessionId: sessionIdSchema.meta(metadata.recordId),
    }),
);

export const SESSION_NOTE_DELETE_TYPE = 'session-note/delete';

export const sessionNoteDelete = defineCommand(
    SESSION_NOTE_DELETE_TYPE,
    z.object({
        id: sessionNoteIdSchema,
        practiceMemberId: practiceMemberIdSchema.meta(metadata.nonSensitive),
    }),
);
